import {
  focusPlayfieldCanvas,
  releaseDomOverlayFocus,
  setPlayerOverlayOpen,
} from "./player-input-gate.js";

export type OpenFollowConfirmOptions = {
  message: string;
  onConfirm: () => void;
  onCancel?: () => void;
};

let uiAbort: AbortController | null = null;
let pendingOnConfirm: (() => void) | undefined;
let pendingOnCancel: (() => void) | undefined;

export function isFollowConfirmOpen(): boolean {
  if (typeof document === "undefined") return false;
  return document.getElementById("follow-confirm-overlay")?.classList.contains("open") === true;
}

export function closeFollowConfirm(): void {
  uiAbort?.abort();
  uiAbort = null;
  pendingOnConfirm = undefined;
  pendingOnCancel = undefined;

  const overlay = document.getElementById("follow-confirm-overlay");
  if (overlay) {
    releaseDomOverlayFocus(overlay);
    overlay.classList.remove("open");
    overlay.setAttribute("aria-hidden", "true");
  }

  setPlayerOverlayOpen("follow-confirm", false);
  focusPlayfieldCanvas();
}

export function openFollowConfirm(opts: OpenFollowConfirmOptions): void {
  if (isFollowConfirmOpen()) return;

  const overlay = document.getElementById("follow-confirm-overlay");
  const bodyEl = document.getElementById("follow-confirm-body");
  const yesBtn = document.getElementById("follow-confirm-yes") as HTMLButtonElement | null;
  const noBtn = document.getElementById("follow-confirm-no") as HTMLButtonElement | null;
  if (!overlay || !bodyEl || !yesBtn || !noBtn) return;

  pendingOnConfirm = opts.onConfirm;
  pendingOnCancel = opts.onCancel;

  bodyEl.textContent = opts.message;
  overlay.classList.add("open");
  overlay.setAttribute("aria-hidden", "false");
  setPlayerOverlayOpen("follow-confirm", true);

  uiAbort = new AbortController();
  const { signal } = uiAbort;

  const dismiss = (confirmed: boolean) => {
    const onConfirm = pendingOnConfirm;
    const onCancel = pendingOnCancel;
    closeFollowConfirm();
    if (confirmed) onConfirm?.();
    else onCancel?.();
  };

  yesBtn.addEventListener(
    "click",
    (ev) => {
      ev?.stopPropagation?.();
      dismiss(true);
    },
    { signal }
  );
  noBtn.addEventListener(
    "click",
    (ev) => {
      ev?.stopPropagation?.();
      dismiss(false);
    },
    { signal }
  );

  const dialog = document.getElementById("follow-confirm-dialog");
  dialog?.addEventListener("click", (ev) => ev.stopPropagation(), { signal });

  overlay.addEventListener(
    "click",
    (ev) => {
      if (ev.target === overlay) dismiss(false);
    },
    { signal }
  );

  document.addEventListener(
    "keydown",
    (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        dismiss(false);
      }
    },
    { signal }
  );

  queueMicrotask(() => yesBtn.focus?.());
}
